import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { tenantThemeService } from '@/services/tenantThemeService';
import { useAuth } from '@/contexts/AuthContext';
import type { CustomTheme } from '@/theme';

export const tenantThemeKeys = {
  all: ['tenantTheme'] as const,
  // "Run as user" changes identity in-app, so the key carries it, as the preferences' key does.
  forUser: (userId: string | undefined) => [...tenantThemeKeys.all, userId ?? ''] as const,
};

/** The tenant's colours as the app reads them: null while the tenant has none. */
export function useTenantTheme(): { customTheme: CustomTheme | null; isLoading: boolean } {
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: tenantThemeKeys.forUser(user?.sub),
    queryFn: () => tenantThemeService.get(),
    staleTime: Infinity,
  });
  return { customTheme: data ?? null, isLoading };
}

export function useUpdateTenantTheme() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (theme: CustomTheme) => tenantThemeService.update(theme),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: tenantThemeKeys.all }),
  });
}
